/**
 * Anime stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const animeStickersPack: ToilePack = {
    slug: 'anime-stickers',
    category: 'sticker',
    name: 'Anime stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'girl', label: 'Chibi girl', subject: 'a chibi girl with big eyes and twin tails waving'},
        {key: 'boy', label: 'Chibi boy', subject: 'a chibi boy holding a wooden sword over his shoulder'},
        {key: 'mascot', label: 'Mascot', subject: 'a round fluffy mascot creature with big eyes and small wings'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a cat-eared mascot instead', 'a whole sticker sheet', 'as a matching pair'],
    directionHint: 'try: a cat-eared mascot instead',
    seo: {
        title: 'Anime Stickers: Chibi Girl, Chibi Boy, Mascot',
        metaDescription: 'Nine anime-style stickers — a chibi girl waving, a chibi boy with a sword, a mascot creature — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Anime stickers: chibi girl, chibi boy, mascot creature',
        tagline: 'Nine stickers, three anime-style characters, three styles each.',
        intro: 'A chibi girl with big eyes and twin tails waving, a chibi boy holding a wooden sword over his shoulder, and a round fluffy mascot creature with small wings — three original anime-style characters, die-cut and ready to print. Each rendered kawaii, bold cartoon, and loose hand-drawn. Ask for a different character, a school scene, or a whole cast composition, and the same three styles carry it.',
        useCases: [
            'a laptop or water bottle',
            'a planner or journal page',
            'a phone case sticker sheet',
            'a convention badge or bag',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine anime-style stickers — a chibi girl, a chibi boy, and a mascot creature — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Are these based on a specific anime series?', a: 'No — all three characters are original designs, not from any existing show or franchise.'},
            {q: 'Can I get a different character or scene?', a: 'Yes — the direction field takes any variation, including a different character, a school scene, or a whole cast composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
        ],
    },
}
